import { Controller } from "@hotwired/stimulus"
import ahoy from 'ahoy.js'

export default class extends Controller {
  static targets = ["container", "slide", "indicator", "slidesContainer", "loadingIndicator"]
  static values = {
    page: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    url: String
  }

  connect() {
    // Set up scroll event listener for both indicator updates and infinite loading
    this.containerTarget.addEventListener('scroll', this.handleScroll.bind(this))

    // Add resize observer to handle layout changes
    this.resizeObserver = new ResizeObserver(() => {
      this.updateIndicators()
    })
    this.resizeObserver.observe(this.containerTarget)

    // Initial update
    this.updateIndicators()

    // Flag to track if we're currently loading more content
    this.isLoading = false

    // Tracking state for horizontal scrolling
    this.scrollStartTime = null
    this.scrollStartPosition = 0
    this.totalScrollDistance = 0
    this.scrollTracked = false
    this.viewedTrainers = new Set()
    this.pendingTrainerViews = []
    this.trackingTimeout = null

    // Track initial visible trainers after a short delay
    setTimeout(() => this.trackVisibleTrainers(), 500)
    
    // If all trainers are already loaded (single page), show marketing message
    if (this.pageValue >= this.totalPagesValue && this.hasLoadingIndicatorTarget) {
      this.showMarketingMessage()
    }
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    this.containerTarget.removeEventListener('scroll', this.handleScroll.bind(this))
  }

  // Handle scroll events for both indicator updates and infinite loading
  handleScroll() {
    this.updateIndicators()
    this.checkForInfiniteLoad()
    this.trackScrolling()
    this.trackVisibleTrainers()
  }

  // Track horizontal scrolling behavior
  trackScrolling() {
    const container = this.containerTarget
    const currentPosition = container.scrollLeft

    // Start tracking if this is the first scroll
    if (this.scrollStartTime === null) {
      this.scrollStartTime = Date.now()
      this.scrollStartPosition = currentPosition
    }

    // Calculate total distance scrolled
    const distanceFromStart = Math.abs(currentPosition - this.scrollStartPosition)
    if (distanceFromStart > this.totalScrollDistance) {
      this.totalScrollDistance = distanceFromStart
    }

    // Clear any existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }

    // Set a timeout to detect when scrolling has stopped
    this.scrollTimeout = setTimeout(() => {
      this.onScrollEnd()
    }, 150)
  }

  // Called when scrolling has stopped
  onScrollEnd() {
    if (this.scrollStartTime === null || this.scrollTracked) return

    const scrollDuration = Date.now() - this.scrollStartTime
    const container = this.containerTarget
    const slideWidth = this.slideTargets.length > 0 ? this.slideTargets[0].offsetWidth : 0
    const slidesScrolled = slideWidth > 0 ? Math.round(this.totalScrollDistance / slideWidth) : 0

    // Only track if user actually scrolled (more than half a slide)
    if (slidesScrolled > 0) {
      ahoy.track('carousel_scrolled', {
        section: 'trainers',
        page: 'home',
        distance_px: Math.round(this.totalScrollDistance),
        slides_scrolled: slidesScrolled,
        duration_ms: scrollDuration,
        method: this.detectScrollMethod(),
        device_type: this.getDeviceType()
      })

      this.scrollTracked = true
    }

    // Reset tracking state for next scroll session
    this.scrollStartTime = null
    this.totalScrollDistance = 0
  }

  // Detect if scroll was via touch/swipe or mouse/button
  detectScrollMethod() {
    // Check if device supports touch
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isMobile = this.getDeviceType() === 'mobile'

    if (hasTouch || isMobile) {
      return 'swipe'
    }
    return 'mouse' // Could be mouse drag, arrow buttons, or trackpad
  }

  // Track which trainers become visible (debounced)
  trackVisibleTrainers() {
    const container = this.containerTarget
    const containerLeft = container.scrollLeft
    const containerRight = containerLeft + container.offsetWidth

    this.slideTargets.forEach((slide, index) => {
      const slideLeft = slide.offsetLeft
      const slideRight = slideLeft + slide.offsetWidth

      // Check if slide is at least 50% visible
      const visibleWidth = Math.min(slideRight, containerRight) - Math.max(slideLeft, containerLeft)
      const visibilityPercent = (visibleWidth / slide.offsetWidth) * 100

      if (visibilityPercent >= 50 && !this.viewedTrainers.has(index)) {
        this.viewedTrainers.add(index)

        // Extract trainer data if available
        const trainerId = slide.querySelector('[data-team-member-id]')?.dataset.teamMemberId
        const trainerName = slide.querySelector('[data-team-member-label]')?.dataset.teamMemberLabel

        // Add to pending views instead of tracking immediately
        this.pendingTrainerViews.push({
          trainer_index: index,
          trainer_id: trainerId,
          trainer_name: trainerName
        })
      }
    })

    // Debounce: only send tracking after scrolling stops
    if (this.trackingTimeout) {
      clearTimeout(this.trackingTimeout)
    }

    this.trackingTimeout = setTimeout(() => {
      this.flushTrainerViews()
    }, 500) // Wait 500ms after scroll stops
  }

  // Send batched trainer views
  flushTrainerViews() {
    if (this.pendingTrainerViews.length === 0) return

    // Send a single batch event with all viewed trainers
    ahoy.track('trainers_viewed', {
      trainers: this.pendingTrainerViews,
      count: this.pendingTrainerViews.length,
      page: 'home',
      section: 'trainers',
      device_type: this.getDeviceType()
    })

    // Clear pending views
    this.pendingTrainerViews = []
  }

  getDeviceType() {
    const width = window.innerWidth
    if (width < 640) return 'mobile'
    if (width < 1024) return 'tablet'
    return 'desktop'
  }

  showMarketingMessage() {
    if (!this.hasLoadingIndicatorTarget) return
    
    // Try to find an existing marketing message in the DOM (server-rendered)
    const existingMessage = this.element.querySelector('[data-marketing-message]')
    if (existingMessage) {
      // Clone the server-rendered message
      const marketingCard = existingMessage.cloneNode(true)
      marketingCard.removeAttribute('hidden')
      this.loadingIndicatorTarget.replaceWith(marketingCard)
      return
    }
    
    // Fallback: If no server-rendered message exists, we shouldn't reach here
    // but just in case, hide the loading indicator
    this.loadingIndicatorTarget.remove()
  }

  // Check if we need to load more trainers
  checkForInfiniteLoad() {
    // Only proceed if we have the necessary targets and values
    if (!this.hasUrlValue || !this.hasSlidesContainerTarget) return
    if (this.isLoading || this.pageValue >= this.totalPagesValue) return
    
    const container = this.containerTarget
    const scrollPosition = container.scrollLeft + container.offsetWidth
    
    // Calculate a dynamic threshold based on slide width
    // This ensures we start loading before reaching the very end
    let threshold = 300
    if (this.slideTargets.length > 0) {
      // Use the width of two slides as the threshold
      const slideWidth = this.slideTargets[0].offsetWidth
      threshold = Math.max(slideWidth * 2, 300)
    }
    
    const scrollThreshold = container.scrollWidth - threshold
    
    if (scrollPosition >= scrollThreshold) {
      this.loadMoreTrainers()
    }
  }

  // Load the next page of trainers
  async loadMoreTrainers() {
    if (this.isLoading) return
    
    this.isLoading = true
    const nextPage = this.pageValue + 1
    
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('page', nextPage)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) throw new Error('Failed to load more trainers')
      
      const html = await response.text()
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      
      // Extract the new slides
      let slidesContainer = tempDiv.querySelector('[data-carousel-target="slidesContainer"]')
      if (!slidesContainer) {
        // Try with the old target name if using the new controller name in the view
        slidesContainer = tempDiv.querySelector('[data-infinite-carousel-target="slidesContainer"]')
        if (!slidesContainer) throw new Error('Could not find slides in response')
      }
      
      // Get only the slide elements (not the loading indicator)
      const slideSelector = '[data-carousel-target="slide"], [data-infinite-carousel-target="slide"]'
      const newSlides = Array.from(slidesContainer.querySelectorAll(slideSelector))
      
      // Add data-index attributes to the new slides
      const startIndex = this.slideTargets.length
      newSlides.forEach((slide, i) => {
        slide.dataset.index = startIndex + i
        // Ensure the slide has the correct target attribute
        if (!slide.hasAttribute('data-carousel-target')) {
          slide.setAttribute('data-carousel-target', 'slide')
        }
      })
      
      // Insert the new slides before the loading indicator or at the end
      if (this.hasLoadingIndicatorTarget) {
        newSlides.forEach(slide => {
          this.slidesContainerTarget.insertBefore(slide, this.loadingIndicatorTarget)
        })
      } else {
        newSlides.forEach(slide => {
          this.slidesContainerTarget.appendChild(slide)
        })
      }
      
      // Update the page value
      this.pageValue = nextPage
      
      // If we've loaded all pages, replace loading indicator with marketing message
      if (this.pageValue >= this.totalPagesValue && this.hasLoadingIndicatorTarget) {
        this.showMarketingMessage()
      }
      
      // Update indicators for the new total count
      this.updateIndicators()
      
    } catch (error) {
      console.error('Error loading more trainers:', error)
    } finally {
      this.isLoading = false
    }
  }

  scrollLeft() {
    const container = this.containerTarget
    const slideWidth = this.slideTargets[0].offsetWidth

    // Track arrow button click
    ahoy.track('carousel_arrow_clicked', {
      direction: 'left',
      section: 'trainers',
      page: 'home',
      device_type: this.getDeviceType()
    })

    // Reset scroll tracking so the next scroll is tracked
    this.scrollTracked = false

    container.scrollBy({
      left: -slideWidth,
      behavior: 'smooth'
    })
  }

  scrollRight() {
    const container = this.containerTarget
    const slideWidth = this.slideTargets[0].offsetWidth

    // Check if we're at or near the end
    const maxScroll = container.scrollWidth - container.clientWidth
    const isNearEnd = container.scrollLeft >= maxScroll - 20

    // If we're at the last page and near the end, don't scroll further
    if (this.pageValue >= this.totalPagesValue && isNearEnd) {
      return
    }

    // Track arrow button click
    ahoy.track('carousel_arrow_clicked', {
      direction: 'right',
      section: 'trainers',
      page: 'home',
      device_type: this.getDeviceType()
    })

    // Reset scroll tracking so the next scroll is tracked
    this.scrollTracked = false

    container.scrollBy({
      left: slideWidth,
      behavior: 'smooth'
    })
  }

  // Go to a slide based on its visible index (for pagination dots)
  goToVisibleSlide(event) {
    const visibleIndex = parseInt(event.currentTarget.dataset.visibleIndex)
    const totalSlides = this.slideTargets.length
    const visibleCount = Math.min(totalSlides, 7) // Match the max in the view
    
    // Calculate the actual slide index based on the visible index
    let actualIndex
    
    if (totalSlides <= visibleCount) {
      // If we're showing all slides, direct mapping
      actualIndex = visibleIndex
    } else {
      // If we have more slides than visible dots, distribute them evenly
      actualIndex = Math.floor((visibleIndex * totalSlides) / visibleCount)
    }
    
    this.goToSlide({ currentTarget: { dataset: { index: actualIndex } } })
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const slide = this.slideTargets[index]
    
    if (slide) {
      this.containerTarget.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth'
      })
    }
  }

  updateIndicators() {
    if (!this.hasIndicatorTarget) return

    const container = this.containerTarget
    const slides = this.slideTargets
    const totalSlides = slides.length
    const visibleCount = Math.min(totalSlides, 7) // Match the max in the view
    
    // Find which slide is most visible in the viewport
    const containerLeft = container.scrollLeft
    const containerWidth = container.offsetWidth
    const containerCenter = containerLeft + (containerWidth / 2)
    
    let closestSlide = 0
    let closestDistance = Infinity
    
    slides.forEach((slide, index) => {
      const slideLeft = slide.offsetLeft
      const slideWidth = slide.offsetWidth
      const slideCenter = slideLeft + (slideWidth / 2)
      const distance = Math.abs(containerCenter - slideCenter)
      
      if (distance < closestDistance) {
        closestDistance = distance
        closestSlide = index
      }
    })
    
    // Calculate which visible indicator should be active
    let activeVisibleIndex
    
    if (totalSlides <= visibleCount) {
      // If we're showing all slides, direct mapping
      activeVisibleIndex = closestSlide
    } else {
      // If we have more slides than visible dots, map the actual index to visible index
      activeVisibleIndex = Math.round((closestSlide * visibleCount) / totalSlides)
    }
    
    // Update the indicators
    this.indicatorTargets.forEach((indicator, index) => {
      if (index === activeVisibleIndex) {
        indicator.classList.remove('bg-gray-300')
        indicator.classList.add('bg-accent-600', 'w-4')
      } else {
        indicator.classList.remove('bg-accent-600', 'w-4')
        indicator.classList.add('bg-gray-300')
      }
    })
    
    // Update the right arrow button state if we're at the end
    const rightArrow = this.element.querySelector('[data-action="click->carousel#scrollRight"]')
    if (rightArrow) {
      const maxScroll = container.scrollWidth - container.clientWidth
      const isNearEnd = container.scrollLeft >= maxScroll - 20
      
      if (this.pageValue >= this.totalPagesValue && isNearEnd) {
        rightArrow.setAttribute('disabled', 'disabled')
        rightArrow.classList.remove('opacity-40', 'hover:opacity-100')
        rightArrow.classList.add('opacity-20')
      } else {
        rightArrow.removeAttribute('disabled')
        rightArrow.classList.remove('opacity-20')
        rightArrow.classList.add('opacity-40', 'hover:opacity-100')
      }
    }
  }
}
